import { PROTOCOL_VERSION, makeEnvelope, isValidEnvelope } from '../postmessage/envelope';
import {
  HOST_TO_IFRAME,
  IFRAME_TO_HOST,
  type ScenePayload,
  type SceneResizePayload,
  type SceneFeedbackPayload,
} from '../postmessage/protocol';

/**
 * Scene surface — renders agent-generated scenes into an element the partner
 * owns.
 *
 * Hosted sites render scenes by REPLACING the page (`SceneLayout.tsx`:
 * "Scene replaces page content (not an overlay)"). embed.js is a guest on a
 * page Mobeus does not own, so that is not available: the partner names an
 * element and we render inside it, nowhere else.
 *
 * An iframe rather than the widget's shadow root, for one reason: the cards
 * are ~200 Tailwind utilities and 5 `--theme-*` tokens deep, and inside
 * `/widget-scene` those come free from the app's own stylesheet. In the
 * shadow root they would have to be recompiled, rescoped around
 * `:host { all: initial }`, and have every viewport breakpoint rewritten. It
 * also means a partner's CSS cannot reach in and break the cards, and ours
 * cannot leak out onto their page.
 *
 * The surface owns no session state. Scenes arrive on the event bus, get
 * forwarded verbatim, and the renderer decides what to do with them.
 */

export interface SceneSurface {
  /** Forward one publisher envelope. Queued if the iframe is still loading. */
  push(scene: ScenePayload): void;
  destroy(): void;
  /** Test seam — the mounted iframe, or null when the selector missed. */
  _getIframeForTests(): HTMLIFrameElement | null;
}

export interface SceneSurfaceOptions {
  baseUrl: string;
  /** CSS selector from `widget.defaults.sceneContainer`. Server-sourced, not
   *  a `data-config` key, so partners change it without republishing. */
  selector: string;
  /** Relays renderer complaints onto the `tele:feedback` data topic so the
   *  agent self-corrects next turn. Host-side because the LiveKit room lives
   *  in embed.js, not in the iframe. */
  onFeedback?: (payload: SceneFeedbackPayload) => void;
}

export function createSceneSurface(opts: SceneSurfaceOptions): SceneSurface | null {
  let container: HTMLElement | null = null;
  try {
    container = document.querySelector<HTMLElement>(opts.selector);
  } catch {
    // An admin typo like `#my scene` throws rather than returning null.
    console.warn(`[Mobeus] scene container "${opts.selector}" is not a valid CSS selector; scenes disabled`);
    return null;
  }

  if (!container) {
    // Deliberately not falling back to the widget's own dock: a multi-column
    // card grid in a 480px column reads as "scenes are broken", which is
    // worse than the honest no-op.
    console.warn(`[Mobeus] scene container "${opts.selector}" not found on the page; scenes disabled`);
    return null;
  }

  // Parse once and check the scheme before anything derived from `baseUrl`
  // reaches `iframe.src`. `data-api-base` is already guarded in bootstrap.ts,
  // but the other derivation — stripping `/embed.js` off the script tag's own
  // `src` — is not, and both land here. A non-http(s) base is a broken embed
  // either way, so refusing is strictly better than mounting something odd.
  let base: URL;
  try {
    base = new URL(opts.baseUrl);
  } catch {
    console.warn(`[Mobeus] scene surface: unusable base URL "${opts.baseUrl}"; scenes disabled`);
    return null;
  }
  if (base.protocol !== 'https:' && base.protocol !== 'http:') {
    console.warn(`[Mobeus] scene surface: base URL must be http(s), got "${base.protocol}"; scenes disabled`);
    return null;
  }
  const allowedOrigin = base.origin;
  let ready = false;
  // Scenes land microseconds apart (skeleton then scene) and the very first
  // pair routinely beats the iframe's load, so hold them rather than drop
  // them. Only the newest matters — an older scene is already superseded.
  let queued: ScenePayload | null = null;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('data-mobeus-scene', '');
  // Built through the URL API rather than string concatenation, so the result
  // is always a parsed absolute http(s) URL. `base.pathname` is preserved for
  // partners behind a reverse proxy that fronts us under a sub-path.
  const sceneUrl = new URL(`${base.pathname.replace(/\/+$/, '')}/widget-scene`, base);
  sceneUrl.search = `v=${PROTOCOL_VERSION}`;
  iframe.src = sceneUrl.toString();
  iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
  // Fills the partner's box horizontally; height is driven by the renderer
  // via SCENE_RESIZE, because only it knows how tall the cards came out.
  iframe.style.cssText = 'display:block;width:100%;height:0;border:none;background:transparent;';

  function post(type: string, payload: unknown) {
    iframe.contentWindow?.postMessage(makeEnvelope('mobeus-host', type, payload), allowedOrigin);
  }

  function messageListener(ev: MessageEvent) {
    if (ev.origin !== allowedOrigin) return;
    if (ev.source !== iframe.contentWindow) return;
    if (!isValidEnvelope(ev.data, 'mobeus-iframe')) return;

    const env = ev.data;
    if (env.type === IFRAME_TO_HOST.READY) {
      ready = true;
      if (queued) {
        post(HOST_TO_IFRAME.SCENE, queued);
        queued = null;
      }
    } else if (env.type === IFRAME_TO_HOST.SCENE_RESIZE) {
      const h = (env.payload as SceneResizePayload | undefined)?.height;
      if (typeof h === 'number' && h >= 0) iframe.style.height = `${h}px`;
    } else if (env.type === IFRAME_TO_HOST.SCENE_FEEDBACK) {
      opts.onFeedback?.(env.payload as SceneFeedbackPayload);
    } else if (env.type === IFRAME_TO_HOST.ERROR) {
      console.error('[Mobeus scene]', env.payload);
    }
  }

  window.addEventListener('message', messageListener);
  container.appendChild(iframe);

  return {
    push(scene) {
      if (!ready) {
        queued = scene;
        return;
      }
      post(HOST_TO_IFRAME.SCENE, scene);
    },
    destroy() {
      window.removeEventListener('message', messageListener);
      iframe.remove();
      queued = null;
      ready = false;
    },
    _getIframeForTests() {
      return iframe;
    },
  };
}
